import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { useFoodicsStatus } from "src/api/integrations/foodics";
import { getFoodicsInstallUrl } from "src/pages/integrations/foodics/constants";
import { getLocalStoredData } from "src/utils/_localStorage";
import { ConstLocalStorage } from "src/constants/_localStorage";
import { notificationsState } from "src/store/notifications";
import { useEffect, useRef, useState } from "react";
import LinkIcon from "@mui/icons-material/Link";
import SettingsIcon from "@mui/icons-material/Settings";
import foodicsLogo from "src/assets/svg-icons/foodics.svg";

const FoodicsIntegrationCard = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const { data: status, isLoading, refetch } = useFoodicsStatus();
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const handleConnect = () => {
    const user = getLocalStoredData(ConstLocalStorage.user);
    const companyId = Number(user?.company?.id ?? user?.company_id ?? 0);
    if (!companyId) {
      return;
    }
    const url = getFoodicsInstallUrl(companyId);
    if (!url) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: generalT("apiBaseUrlMissing"),
        },
      ]);
      return;
    }
    window.location.assign(url);
    setIsPolling(true);
  };

  useEffect(() => {
    if (isPolling && !status?.connected) {
      pollingRef.current = setInterval(() => {
        refetch();
      }, 2500);
    }

    if (status?.connected && isPolling) {
      setIsPolling(false);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isPolling, status?.connected, refetch]);

  const handleManage = () => {
    navigate("/integrations/foodics");
  };

  if (isLoading) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: "12px",
          height: "100%",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </Card>
    );
  }

  const isConnected = status?.connected && status?.is_active;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "12px",
        height: "100%",
        transition: "box-shadow 0.2s",
        "&:hover": {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box
              component="img"
              src={foodicsLogo}
              alt="Foodics"
              width={{ xs: 40, sm: 48 }}
              height={{ xs: 40, sm: 48 }}
              sx={{ objectFit: "contain" }}
            />
            <Chip
              size="small"
              label={
                isConnected
                  ? t("status.connected")
                  : t("status.disconnected")
              }
              color={isConnected ? "success" : "default"}
              sx={{
                fontWeight: 500,
                fontSize: { xs: "11px", sm: "12px" },
                height: { xs: 24, sm: 28 },
              }}
            />
          </Box>

          {/* Title & Description */}
          <Box>
            <Typography
              fontSize={{ xs: 16, sm: 18 }}
              fontWeight={600}
              color="#101828"
            >
              {t("foodics.title")}
            </Typography>
            <Typography
              fontSize={{ xs: 13, sm: 14 }}
              color="#667085"
              mt={0.5}
            >
              {!isConnected && status?.install_uses_dev_token
                ? t("foodics.connectPromptDev")
                : t("foodics.description")}
            </Typography>
          </Box>

          {/* Connection Info */}
          {isConnected && status?.business_name && (
            <Stack
              direction={{ xs: "row", sm: "column" }}
              spacing={{ xs: 2, sm: 0 }}
              bgcolor="#F9FAFB"
              borderRadius="8px"
              p={{ xs: 1.5, sm: 1.5 }}
              border="1px solid #EAECF0"
              flexWrap="wrap"
            >
              <Box flex={1} minWidth={0}>
                <Typography fontSize={{ xs: 11, sm: 12 }} color="#667085">
                  {t("foodics.businessName")}
                </Typography>
                <Typography
                  fontSize={{ xs: 13, sm: 14 }}
                  fontWeight={500}
                  color="#344054"
                  noWrap
                >
                  {status.business_name}
                </Typography>
              </Box>
              {status.connected_at && (
                <Box flex={1} minWidth={0}>
                  <Typography fontSize={{ xs: 11, sm: 12 }} color="#667085">
                    {t("foodics.connectedAt")}
                  </Typography>
                  <Typography
                    fontSize={{ xs: 13, sm: 14 }}
                    fontWeight={500}
                    color="#344054"
                  >
                    {new Date(status.connected_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}

          {/* Actions */}
          <Stack direction="column" spacing={1} mt={1}>
            {isConnected ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    <SettingsIcon sx={{ fontSize: "16px !important" }} />
                  }
                  onClick={handleManage}
                  fullWidth
                  sx={{
                    fontSize: "12px",
                    py: 1,
                    "& .MuiButton-startIcon": {
                      marginRight: 0.5,
                    },
                  }}
                >
                  {t("foodicsHub.openWorkspace")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate("/integrations/foodics/menu")}
                  fullWidth
                  sx={{
                    fontSize: "12px",
                    py: 1,
                  }}
                >
                  {t("foodicsHub.cardMenuTitle")}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                fullWidth
                size="small"
                startIcon={
                  <LinkIcon sx={{ fontSize: "16px !important" }} />
                }
                onClick={handleConnect}
                sx={{
                  fontSize: "12px",
                  py: 1,
                }}
              >
                {t("actions.connect")}
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default FoodicsIntegrationCard;
